import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { Button, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import { getNotifications, updateNotification } from "../services/notificationApi";
import DetailNotification from "../component/DetailNotification";
import { PiChecks } from "react-icons/pi";

const AllNotifications = () => {
  
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  document.title = `Notifications | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const { isLoading, isError, error, data: notifications } = useQuery("notifications", getNotifications);

  const toggleDetailModal = useCallback((notification = null) => {
    setSelectedNotification(notification);
    setIsDetailModalOpen(prev => !prev);
  },[updateNotification]);

  const handleDetail = async(notification) => {
    toggleDetailModal(notification);
    if(notification) {
      if (notification.status !== "Lue") {
        await updateNotification(notification.id, "Lue");
        await queryClient.invalidateQueries("notifications");
      }
    }
  };

  const columns = useMemo(() => [
    { Header: "Numéro", accessor: "id", filterable: false },
    { Header: "Titre", accessor: "title", filterable: false },
    { Header: "Type", accessor: "type", filterable: false },
    { 
      Header: "Chauffeur",
      accessor: row => row.user?.name,
      filterable: false 
    },
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ value }) => value ? new Date(value).toLocaleDateString('fr-FR') : "-",
      filterable: false,
    },
    {
      Header: "Statut",
      accessor: "status",
      Cell: ({ value }) => (
        <span className={`badge bg-${value === "Lue" ? "success" : "danger"}-subtle text-${value === "Lue" ? "success" : "danger"}`}>
          {value}
        </span>
      ),
      filterable: false,
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            title="Voir"
            size="sm"
            onClick={() => handleDetail(row.original)}
            color="primary"
            aria-label="Lire la notification"
          >
            <PiChecks />
          </Button>
        </div>
      ),
      filterable: false,
    },
  ], [handleDetail]);

  const tabs = ['Toutes', 'Lue', 'Non lue'];
  const [activeTab, setActiveTab] = useState('Toutes');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3 mb-3">
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`btn ${activeTab === tab ? 'active-tab' : 'btn-light'}`}
          >
            {tab}
          </Button>
        ))}
      </div>
    </div>
  );

  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    switch (activeTab) {
      case 'Lue':
        return notifications.filter(notification => notification.status === 'Lue');
      case 'Non lue':
        return notifications.filter(notification => notification.status === 'Non lue');
      default:
        return notifications;
    }
  }, [notifications, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Notifications" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={filteredNotifications}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement des notifications 🔔, Merci de patienter ... <Spin />
                    </div>
                  )}
                  {filteredNotifications.length === 0 && !isLoading && !isError && (
                    <div className="text-center">Aucune notification {activeTab} à afficher pour le moment 😌.</div>
                  )}
                  {isError && !isLoading && (
                    <div className="text-center">Erreur lors du chargement des notifications : {error.message}</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <DetailNotification
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedNotification={selectedNotification}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AllNotifications;
